// Badges tab: the product's own badges (key, label in the editing language,
// color) and the day it stops being "New". The computed New badge shows on the
// product while that day is ahead; a badge keyed `new` names and colors it.
import React from "react"
import { LanguageTag } from "aldoo-ra/Admin/Store/Products/EditingLanguage"
import { PlusIcon, TrashIcon, XMarkIcon, CalendarIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import DatePicker from "aldoo-ra/DatePicker"
import ColorPickerEditor from "aldoo-ra/CMS/field-editors/color-picker"
import { Badge } from "aldoo-ra/Store/extensions/badges/storefront"

const input = "w-full h-10 px-3 rounded-lg border border-admin_bordered bg-admin_paper text-admin_text text-sm outline-none focus:border-admin_primary"

// the badges every fashion store ends up with, one click away
const PRESETS = [
  { key: "outlet", label: "Outlet", color: "#dc2626" },
  { key: "4xl", label: "до 4XL", color: "#111111" },
  { key: "new", label: "New", color: "#16a34a" },
]

/** A label as a { language: text } map (an imported one may be a plain text). */
const asMap = (label) => {
  if (label && typeof label === "object" && !Array.isArray(label)) return label
  return typeof label === "string" && label ? { en: label } : {}
}

const pad = (n) => String(n).padStart(2, "0")
const dayOf = (value) => {
  if (!value) return ""
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? "" : `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
}

export default function BadgesTab({ value = {}, onChange, language = "en" }) {
  const badges = Array.isArray(value.badges) ? value.badges : []
  const newUntil = value.newUntil || null
  const lang = language || "en"
  const isNew = newUntil && new Date(newUntil).getTime() > Date.now()

  const setBadges = (next) => onChange?.({ badges: next })
  const update = (index, patch) => setBadges(badges.map((badge, i) => (i === index ? { ...badge, ...patch } : badge)))
  const setLabel = (index, text) => update(index, { label: { ...asMap(badges[index]?.label), [lang]: text } })
  const add = (preset = { key: "", label: "", color: "#111111" }) => setBadges([...badges, { key: preset.key, label: preset.label ? { [lang]: preset.label } : {}, color: preset.color }])
  const remove = (index) => setBadges(badges.filter((_, i) => i !== index))
  // the day picked is new through its end
  const setNewUntil = (date) => {
    if (!date) return onChange?.({ newUntil: null })
    const day = new Date(date)
    onChange?.({ newUntil: new Date(day.getFullYear(), day.getMonth(), day.getDate(), 23, 59, 59, 999).toISOString() })
  }

  return (
    <div className="flex flex-col gap-8" data-testid="badges-tab">
      <section className="flex flex-col gap-2">
        <Typography variant="admin_body1" className="font-semibold my-0">
          New until
        </Typography>
        <Typography variant="admin_body2" className="opacity-70 my-0">
          The product shows the “New” badge through this day.
        </Typography>
        <div className="flex items-center gap-3">
          <div className="relative w-full max-w-[260px]">
            <DatePicker
              variant="admin"
              value={newUntil}
              onChange={setNewUntil}
              dateFormat="DD.MM.YYYY"
              triggerClassName="block w-full cursor-pointer"
              trigger={
                <span className={`${input} flex items-center gap-2`}>
                  <CalendarIcon className="w-5 h-5 shrink-0 text-admin_text_secondary" />
                  <span className={newUntil ? "text-admin_text" : "text-admin_text_secondary"}>{dayOf(newUntil) || "Not new"}</span>
                </span>
              }
            />
            {newUntil && (
              <button type="button" onClick={() => setNewUntil(null)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-admin_text_secondary hover:text-admin_error" aria-label="Clear the date">
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          {isNew && <Badge badge={{ key: "new", label: "New", color: badges.find((b) => b?.key === "new")?.color || "#16a34a" }} size="sm" />}
        </div>
      </section>

      <section className="flex flex-col gap-3">
        <Typography variant="admin_body1" className="font-semibold my-0">
          Badges
        </Typography>
        <Typography variant="admin_body2" className="opacity-70 my-0">
          Shown over the product's pictures, in this order. The label is edited in the language picked at the top of the dialog.
        </Typography>
        {badges.map((badge, index) => {
          const labels = asMap(badge?.label)
          const others = Object.entries(labels).filter(([code, text]) => code !== lang && text)
          return (
            <div key={index} className="flex flex-col md:flex-row md:items-start gap-3 p-3 border border-admin_bordered rounded-lg" data-testid="badge-row">
              <label className="flex flex-col gap-1 md:w-[160px]">
                <span className="text-xs text-admin_text_secondary">Key</span>
                <input className={input} value={badge?.key || ""} placeholder="outlet" onChange={(e) => update(index, { key: e.target.value.trim().toLowerCase() })} />
              </label>
              <label className="flex flex-col gap-1 flex-1">
                <span className="inline-flex items-center gap-1.5 text-xs text-admin_text_secondary">
                  Label <LanguageTag language={lang} />
                </span>
                <input className={input} value={labels[lang] || ""} placeholder={labels.en || "Outlet"} onChange={(e) => setLabel(index, e.target.value)} />
                {others.length > 0 && <span className="text-xs text-admin_text_secondary">{others.map(([code, text]) => `${code}: ${text}`).join(" · ")}</span>}
              </label>
              <div className="flex flex-col gap-1 md:w-[200px]">
                <span className="text-xs text-admin_text_secondary">Color</span>
                <ColorPickerEditor value={badge?.color || ""} onChange={(color) => update(index, { color })} />
              </div>
              <div className="flex items-center gap-3 md:pt-6">
                <Badge badge={{ key: badge?.key, label: labels[lang] || labels.en || badge?.key || "—", color: badge?.color }} size="sm" />
                <button type="button" onClick={() => remove(index)} className="p-2 rounded text-admin_text_secondary hover:text-admin_error" aria-label="Remove the badge">
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          )
        })}
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="admin_secondary" onClick={() => add()} data-testid="badge-add">
            <PlusIcon className="w-4 h-4 mr-1 inline" />
            Badge
          </Button>
          {PRESETS.filter((preset) => !badges.some((badge) => badge?.key === preset.key)).map((preset) => (
            <Button key={preset.key} variant="admin_text" onClick={() => add(preset)}>
              + {preset.label}
            </Button>
          ))}
        </div>
      </section>
    </div>
  )
}
